import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Loader2, GraduationCap, Sparkles, Calendar, CheckCircle2, X, ArrowRight, Brain, Trophy, Download, LogIn } from 'lucide-react';
import { PROGRESSION } from '../data/progressionAutoEcole';
import { MOTO_PROGRESSION } from '../data/progressionMoto';
import StepVisualModal from '../components/StepVisualModal';
import { getStepSvg } from '../data/stepSvgMap';
import { useAuth } from '../context/AuthContext';
import { celebrate } from '../lib/celebrate';
import { haptic } from '../lib/paliers';

// Index rapide id → label
const STEP_MAP = (() => {
  const m = {};
  (PROGRESSION || []).forEach((phase) => (phase.steps || phase.etapes || []).forEach((s) => { m[s.id] = s; }));
  (MOTO_PROGRESSION || []).forEach((phase) => (phase.steps || []).forEach((s) => { m[s.id] = s; }));
  return m;
})();

const STATUS_META = {
  vu:       { label: 'Observé',  icon: '👁', chip: 'bg-sky-500/15 border-sky-500/50 text-sky-200',        border: 'border-sky-500/30',      title: 'Observé' },
  compris:  { label: 'Compris',  icon: '💡', chip: 'bg-violet-500/15 border-violet-500/50 text-violet-200', border: 'border-violet-500/30',  title: 'Compris' },
  acquis:   { label: 'Acquis',   icon: '🟢', chip: 'bg-emerald-500/15 border-emerald-500/50 text-emerald-200', border: 'border-emerald-500/30', title: 'Acquis' },
  a_revoir: { label: 'À revoir', icon: '🟠', chip: 'bg-amber-500/15 border-amber-500/50 text-amber-200',      border: 'border-amber-500/30',   title: 'À revoir' },
  a_travailler: { label: 'À travailler', icon: '📚', chip: 'bg-sky-500/15 border-sky-500/50 text-sky-200', border: 'border-sky-500/30', title: 'À préparer' },
};

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch (_) { return iso; }
}

export default function PublicFlashSeancePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, openRegister } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [qcm, setQcm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [showAnswers, setShowAnswers] = useState(false);
  const [visualStep, setVisualStep] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [selfEvals, setSelfEvals] = useState({});
  const [selfSending, setSelfSending] = useState(false);
  const [selfSent, setSelfSent] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError('');
    api.flashSeancePublic(token)
      .then((d) => { setData(d); if (d.self_eval) { setSelfEvals(d.self_eval); setSelfSent(true); } })
      .catch((e) => setError(e?.response?.data?.detail || 'Fiche introuvable ou expirée.'))
      .finally(() => setLoading(false));
    api.flashSeanceQcm(token).then((d) => setQcm(d?.questions || [])).catch(() => {});
  }, [token]);

  const claimToLivret = async () => {
    if (!user || user.role !== 'eleve') return;
    setClaiming(true);
    haptic([15, 30, 15]);
    try {
      const res = await api.flashSeanceClaim(token);
      setClaimed(true);
      // 🎉 Confetti + auto-redirect
      celebrate();
      setTimeout(() => celebrate(), 350);
      setTimeout(() => celebrate(), 700);
      haptic([80, 60, 80, 60, 200]);
      setTimeout(() => navigate('/cours/progression'), 2200);
    } catch (e2) {
      alert(e2?.response?.data?.detail || 'Impossible d\'intégrer la séance');
    } finally {
      setClaiming(false);
    }
  };

  const selectAnswer = (qid, idx) => {
    if (showAnswers) return;
    setAnswers((prev) => ({ ...prev, [qid]: idx }));
  };
  const submitQcm = async () => {
    setShowAnswers(true);
    // Si élève connecté : persiste le score (badge collectionné)
    if (user && user.role === 'eleve' && qcm && qcm.length > 0) {
      try {
        const s = qcm.filter((q) => answers[q.id] === q.correct_index).length;
        const res = await api.flashSeanceQcmResult(token, s, qcm.length);
        // 🎉 Confetti + burst si sans-faute
        if (res && res.is_perfect) {
          celebrate();
          setTimeout(() => celebrate(), 300);
          setTimeout(() => celebrate(), 600);
          haptic([80, 60, 80, 60, 200]);
        } else {
          haptic(30);
        }
      } catch (_) { /* silencieux — pas bloquant */ }
    }
  };
  const resetQcm = () => { setAnswers({}); setShowAnswers(false); };
  const score = qcm ? qcm.filter((q) => answers[q.id] === q.correct_index).length : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] text-white flex items-center justify-center">
        <div className="flex items-center gap-3 text-white/60"><Loader2 size={20} className="animate-spin" /><span>Chargement de ta séance…</span></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] text-white flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-red-500/15 border border-red-500/40 text-red-400 flex items-center justify-center mx-auto mb-4"><X size={26} /></div>
          <h1 className="font-serif-editorial text-white text-[26px] leading-tight">Fiche indisponible</h1>
          <p className="text-white/60 text-[13.5px] mt-2 leading-relaxed">{error}</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white font-bold text-[13.5px]"><ArrowRight size={14} /> Aller sur Code Voiture</Link>
        </div>
      </div>
    );
  }

  // Regroupe les steps par statut
  const groups = { acquis: [], compris: [], vu: [], a_revoir: [], a_travailler: [] };
  (data.steps || []).forEach((s) => { if (groups[s.status]) groups[s.status].push(s); });
  const isPrep = data.kind === 'prep';

  const submitSelfEval = async () => {
    if (selfSending || Object.keys(selfEvals).length === 0) return;
    setSelfSending(true);
    try {
      await api.flashSelfEval(token, selfEvals);
      setSelfSent(true);
      celebrate();
      haptic([40, 30, 80]);
    } catch (e2) {
      alert(e2?.response?.data?.detail || 'Envoi impossible — réessaie.');
    } finally { setSelfSending(false); }
  };

  const isLoggedStudent = user && user.role === 'eleve';

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white" data-testid="public-flash-seance">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/40 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white font-serif-editorial text-[18px]" data-testid="pfs-home">
            <span className="w-8 h-8 rounded-full bg-red-500/15 border border-red-500/40 text-red-400 flex items-center justify-center"><GraduationCap size={16} /></span>
            Code Voiture
          </Link>
          <span className="text-white/40 text-[10.5px] font-mono-spec uppercase tracking-widest">Séance flash</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8 sm:py-10">
        {/* Hero */}
        <div className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-red-500/10 via-transparent to-transparent p-5 sm:p-7 overflow-hidden">
          <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-red-500/10 blur-3xl" />
          <div className="relative">
            <div className={`inline-flex items-center gap-2 text-[10.5px] font-mono-spec uppercase tracking-[0.22em] font-bold ${isPrep ? 'text-sky-400' : 'text-red-400'}`}><Sparkles size={12} /> {isPrep ? 'Ton prochain cours' : 'Ta séance du jour'}</div>
            <h1 className="mt-2 font-serif-editorial text-white text-[28px] sm:text-[36px] leading-[1.05]" data-testid="pfs-title">
              {isPrep ? 'À réviser avant ton prochain cours' : 'Récap de ta séance'}
            </h1>
            <div className="mt-3 flex items-center gap-3 text-white/60 text-[13px] flex-wrap">
              <span className="inline-flex items-center gap-1.5"><Calendar size={13} /> {fmtDate(data.created_at)}</span>
              <span className="w-1 h-1 rounded-full bg-white/30" />
              <span>avec <b className="text-white">{data.teacher_name}</b></span>
            </div>
            {data.notes && (
              <div className="mt-5 rounded-lg border border-white/10 bg-white/[0.03] p-4 text-white/80 text-[14px] leading-relaxed italic" data-testid="pfs-notes">
                <div className="text-[10px] font-mono-spec uppercase tracking-widest text-white/40 not-italic mb-1.5">{isPrep ? 'Ce que ton enseignant attend de toi' : 'Le mot de ton enseignant'}</div>
                « {data.notes} »
              </div>
            )}
          </div>
        </div>

        {/* Intégration au suivi — seulement si élève connecté */}
        {isLoggedStudent && !claimed && (
          <div className="mt-6 rounded-2xl border border-emerald-500/40 bg-emerald-500/[0.07] p-5 sm:p-6" data-testid="pfs-claim-block">
            <div className="flex items-center gap-2 text-emerald-400 text-[10.5px] font-mono-spec uppercase tracking-[0.22em] font-bold"><Sparkles size={12} /> Élève connecté</div>
            <div className="mt-1.5 font-serif-editorial text-white text-[22px] leading-tight">Bonjour {user.name?.split(' ')[0]}, ajoute cette séance à ton suivi</div>
            <p className="text-white/60 text-[12.5px] mt-1.5 leading-relaxed">{isPrep ? 'Les thèmes à réviser s\u2019afficheront sur ton tableau de bord pour que tu saches quoi préparer.' : 'Les compétences vues aujourd\u2019hui seront automatiquement mises à jour dans ton livret et ta page « Ma progression ».'}</p>
            <button
              onClick={claimToLivret}
              disabled={claiming}
              data-testid="pfs-claim-btn"
              className="mt-4 h-12 px-6 rounded-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold text-[14px] inline-flex items-center gap-2 shadow-lg shadow-emerald-500/25"
            >
              {claiming ? <><Loader2 size={16} className="animate-spin" /> Import…</> : <><Download size={16} /> Intégrer à mon suivi</>}
            </button>
          </div>
        )}
        {claimed && (
          <div className="mt-6 rounded-2xl border border-emerald-500/50 bg-emerald-500/10 p-5 text-center" data-testid="pfs-claimed">
            <div className="text-4xl mb-2">🎉</div>
            <div className="font-serif-editorial text-white text-[22px]">Ta séance a été ajoutée à ton suivi !</div>
            <div className="text-white/60 text-[12.5px] mt-1">Redirection vers ta progression…</div>
          </div>
        )}

        {/* Cours vus, groupés par statut */}
        <section className="mt-8">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 flex items-center justify-center"><CheckCircle2 size={16} /></div>
            <h2 className="font-serif-editorial text-white text-[22px] leading-tight">{isPrep ? `Thèmes à réviser (${data.steps.length})` : `Compétences travaillées (${data.steps.length})`}</h2>
          </div>
          <p className="text-white/50 text-[12.5px] mb-4">{isPrep ? 'Clique sur un thème pour ouvrir le visuel interactif et le réviser avant la séance.' : 'Clique sur un item pour rouvrir le visuel interactif et réviser.'}</p>

          {['acquis', 'compris', 'vu', 'a_revoir', 'a_travailler'].map((st) => {
            const list = groups[st];
            if (!list || list.length === 0) return null;
            const meta = STATUS_META[st];
            return (
              <div key={st} className={`mb-4 rounded-xl border ${meta.border} bg-white/[0.02] p-3`} data-testid={`pfs-group-${st}`}>
                <div className={`inline-flex items-center gap-1.5 text-[10.5px] font-mono-spec uppercase tracking-widest font-bold rounded-full px-2.5 py-0.5 border ${meta.chip}`}>
                  {meta.icon} {meta.title} · {list.length}
                </div>
                <div className="mt-2.5 grid gap-1.5">
                  {list.map((s) => {
                    const step = STEP_MAP[s.id];
                    const label = step?.label || s.id;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => step && setVisualStep(step)}
                        data-testid={`pfs-step-${s.id}`}
                        className="text-left rounded-lg border border-white/10 hover:border-white/25 bg-white/[0.02] hover:bg-white/[0.05] p-3 flex items-center gap-3 transition-colors group"
                      >
                        <span className="font-mono-spec text-white/70 font-bold text-[12.5px] w-11 shrink-0">{s.id}</span>
                        <span className="text-white text-[13px] leading-tight flex-1">{label}</span>
                        <ArrowRight size={15} className="text-white/30 group-hover:text-white/60" />
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </section>

        {/* Les + / Les − du moniteur */}
        {!isPrep && ((data.plus || []).length > 0 || (data.moins || []).length > 0) && (
          <section className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="pfs-plus-moins">
            {(data.plus || []).length > 0 && (
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.06] p-4">
                <div className="text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-widest font-bold mb-2">👍 Tes points forts</div>
                <ul className="space-y-1.5">
                  {data.plus.map((p, i) => <li key={i} className="text-white/85 text-[13px] flex gap-2"><span className="text-emerald-400">•</span>{p}</li>)}
                </ul>
              </div>
            )}
            {(data.moins || []).length > 0 && (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4">
                <div className="text-amber-300 text-[10.5px] font-mono-spec uppercase tracking-widest font-bold mb-2">⚠️ À travailler</div>
                <ul className="space-y-1.5">
                  {data.moins.map((m, i) => <li key={i} className="text-white/85 text-[13px] flex gap-2"><span className="text-amber-400">•</span>{m}</li>)}
                </ul>
              </div>
            )}
          </section>
        )}

        {/* Proposition du prochain cours */}
        {!isPrep && data.next_step && (
          <section className="mt-4 rounded-xl border border-sky-500/35 bg-sky-500/[0.07] p-4 sm:p-5" data-testid="pfs-next-step">
            <div className="text-sky-300 text-[10.5px] font-mono-spec uppercase tracking-widest font-bold mb-1.5">🎯 Prochain cours proposé</div>
            <div className="flex items-center gap-3">
              <span className="font-mono-spec text-sky-200 font-bold text-[15px] shrink-0">{data.next_step.id}</span>
              <span className="text-white text-[14.5px] leading-snug">{data.next_step.label || STEP_MAP[data.next_step.id]?.label || ''}</span>
            </div>
            <p className="text-white/50 text-[12px] mt-2">Selon ton plan de progression — ton enseignant confirmera lors de la prochaine leçon.</p>
          </section>
        )}

        {/* Auto-évaluation élève — ton ressenti */}
        {!isPrep && (data.steps || []).length > 0 && (
          <section className="mt-8" data-testid="pfs-self-eval">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-md bg-rose-500/15 border border-rose-500/40 text-rose-400 flex items-center justify-center">🙋</div>
              <h2 className="font-serif-editorial text-white text-[22px] leading-tight">Et toi, tu t&apos;en sens où ?</h2>
            </div>
            <p className="text-white/50 text-[12.5px] mb-4">
              {selfSent
                ? `Ton ressenti a été transmis à ${data.teacher_name} ✅ Tu peux le modifier et renvoyer si besoin.`
                : `Donne ton propre ressenti sur chaque compétence — ${data.teacher_name} le verra et pourra adapter ta prochaine leçon.`}
            </p>
            <div className="space-y-2">
              {(data.steps || []).map((s) => {
                const step = STEP_MAP[s.id];
                const mine = selfEvals[s.id];
                return (
                  <div key={s.id} className="rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid={`pfs-self-${s.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono-spec text-white/70 font-bold text-[12px] w-9 shrink-0">{s.id}</span>
                      <span className="text-white text-[12.5px] leading-tight flex-1 min-w-[160px]">{step?.label || s.id}</span>
                      <span className="text-white/35 text-[10.5px] font-mono-spec shrink-0" title="Évaluation de ton enseignant">Prof : {STATUS_META[s.status]?.icon}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {['vu', 'compris', 'acquis', 'a_revoir'].map((st) => {
                        const m = STATUS_META[st];
                        const active = mine === st;
                        return (
                          <button key={st} type="button" data-testid={`pfs-self-${s.id}-${st}`}
                            onClick={() => setSelfEvals((prev) => { const n = { ...prev }; if (n[s.id] === st) delete n[s.id]; else n[s.id] = st; return n; })}
                            className={`h-8 px-2.5 rounded-full border text-[11px] font-semibold transition-colors ${active ? m.chip + ' ring-1 ring-white/25' : 'bg-white/[0.03] border-white/10 text-white/50 hover:border-white/25'}`}>
                            {m.icon} {m.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <button
              type="button"
              onClick={submitSelfEval}
              disabled={selfSending || Object.keys(selfEvals).length === 0}
              data-testid="pfs-self-submit"
              className="mt-4 w-full h-12 rounded-full bg-rose-600 hover:bg-rose-500 disabled:opacity-40 text-white font-bold text-[14px] inline-flex items-center justify-center gap-2"
            >
              {selfSending ? <Loader2 size={16} className="animate-spin" /> : selfSent ? '🔄 Renvoyer mon ressenti' : '📨 Envoyer mon ressenti à mon enseignant'}
            </button>
            {selfSent && !selfSending && (
              <div className="mt-2 text-center text-emerald-300 text-[12px] font-semibold" data-testid="pfs-self-sent">✅ Ressenti transmis — merci !</div>
            )}
          </section>
        )}

        {/* Mini-QCM */}
        {qcm && qcm.length > 0 && (
          <section className="mt-10" data-testid="pfs-qcm">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-md bg-violet-500/15 border border-violet-500/40 text-violet-400 flex items-center justify-center"><Brain size={16} /></div>
              <h2 className="font-serif-editorial text-white text-[22px] leading-tight">Petit test à froid</h2>
            </div>
            <p className="text-white/50 text-[12.5px] mb-4">{qcm.length} questions rapides pour vérifier que ça a bien décanté.</p>

            <div className="space-y-4">
              {qcm.map((q, qi) => (
                <div key={q.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid={`pfs-q-${qi}`}>
                  <div className="text-[11px] font-mono-spec uppercase tracking-widest text-white/40">Question {qi + 1}</div>
                  <div className="mt-1 text-white text-[14.5px] leading-snug font-semibold">{q.question}</div>
                  <div className="mt-3 space-y-1.5">
                    {q.options.map((opt, oi) => {
                      const chosen = answers[q.id] === oi;
                      const correct = q.correct_index === oi;
                      let cls = 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06] text-white/80';
                      if (showAnswers) {
                        if (correct) cls = 'border-emerald-500/50 bg-emerald-500/10 text-white';
                        else if (chosen && !correct) cls = 'border-red-500/50 bg-red-500/10 text-white';
                        else cls = 'border-white/10 bg-white/[0.02] text-white/50';
                      } else if (chosen) {
                        cls = 'border-violet-500/60 bg-violet-500/15 text-white';
                      }
                      return (
                        <button
                          key={oi}
                          type="button"
                          disabled={showAnswers}
                          onClick={() => selectAnswer(q.id, oi)}
                          data-testid={`pfs-q-${qi}-opt-${oi}`}
                          className={`w-full text-left px-3 py-2.5 rounded-lg border text-[13.5px] leading-snug transition-colors ${cls}`}
                        >
                          <span className="font-mono-spec text-[10px] uppercase mr-2 opacity-60">{String.fromCharCode(65 + oi)}.</span>
                          {opt}
                          {showAnswers && correct && <span className="ml-2 text-emerald-400">✓</span>}
                          {showAnswers && chosen && !correct && <span className="ml-2 text-red-400">✗</span>}
                        </button>
                      );
                    })}
                  </div>
                  {showAnswers && q.explanation && (
                    <div className="mt-3 text-[12.5px] text-white/60 leading-relaxed bg-white/[0.03] rounded-lg p-2.5 border border-white/10">
                      <b className="text-white/80">Explication : </b>{q.explanation}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-5 flex gap-2">
              {!showAnswers ? (
                <button
                  onClick={submitQcm}
                  disabled={Object.keys(answers).length < qcm.length}
                  data-testid="pfs-qcm-submit"
                  className="flex-1 h-12 rounded-full bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-bold text-[14px] inline-flex items-center justify-center gap-2"
                >
                  <Trophy size={16} /> Voir mes résultats
                </button>
              ) : (
                <>
                  <div className="flex-1 h-12 rounded-full bg-violet-500/15 border border-violet-500/40 text-white font-bold text-[15px] inline-flex items-center justify-center gap-2" data-testid="pfs-qcm-score">
                    Score : <span className="text-violet-300">{score}/{qcm.length}</span>
                  </div>
                  <button
                    onClick={resetQcm}
                    data-testid="pfs-qcm-reset"
                    className="h-12 px-5 rounded-full bg-white/10 hover:bg-white/15 text-white text-[13px] font-semibold"
                  >
                    Recommencer
                  </button>
                </>
              )}
            </div>
          </section>
        )}

        {/* CTA compte — DISCRET, uniquement si pas connecté */}
        {!isLoggedStudent && (
          <section className="mt-14 mb-4 text-center" data-testid="pfs-cta-register">
            <div className="inline-flex flex-col items-center max-w-md mx-auto">
              <div className="w-1 h-8 bg-gradient-to-b from-transparent to-white/20" />
              <p className="text-white/50 text-[12.5px] leading-relaxed">
                Envie de garder cette séance dans un vrai suivi ?
                <br />
                Ta prochaine séance flash s&apos;intègrera automatiquement à ton livret et ta progression.
              </p>
              <button
                onClick={() => { if (openRegister) openRegister(); else navigate('/'); }}
                data-testid="pfs-register-cta"
                className="mt-3 inline-flex items-center gap-1.5 text-red-400 hover:text-red-300 text-[12.5px] font-semibold border-b border-red-400/40 hover:border-red-300 pb-0.5 transition-colors"
              >
                <LogIn size={12} /> Créer mon compte gratuit en 30 s
              </button>
            </div>
          </section>
        )}

        <footer className="mt-12 text-center text-white/30 text-[11px]">
          Lien valable jusqu&apos;au {fmtDate(data.expires_at)} · Code Voiture · <Link to="/" className="hover:text-white/60 underline">codevoiture.fr</Link>
        </footer>
      </main>

      {/* Modal visuel */}
      {visualStep && (
        <StepVisualModal
          open={!!visualStep}
          onClose={() => setVisualStep(null)}
          step={visualStep}
          svgUrl={getStepSvg(visualStep.id)}
          currentStatus="a_revoir"
          onStatus={() => {}}
        />
      )}
    </div>
  );
}

